import React from "react"
import ReactDOM from "react-dom/client"
import "./index.css"
import { RouterProvider } from "react-router-dom"
import { AuthProvider } from "./hooks/AuthContext"
// import { MantineContextProvider } from './common/lib/mantine/mantineContext'
import { browserRouter } from "./routes/router"
import { QueryClientProvider } from "@tanstack/react-query"
import { queryClient } from "./api/tsQueryConfig"
import { ReactQueryDevtools } from "@tanstack/react-query-devtools"
import "@/common/lib/firebase/firebase"
import { Toaster } from "react-hot-toast"

import * as Sentry from "@sentry/react"
import { getReleaseVersion, logVersionInfo } from "./utils/version"

// Recovery and reset URLs carry single-use credentials. Do not initialize replay/tracing on them.
const sensitiveAuthPage = [
    "/auth/recovery/confirm",
    "/auth/reset-password",
].includes(window.location.pathname)
if (import.meta.env.MODE === "production" && !sensitiveAuthPage) {
    // wake up the mail server (render free-tier workaround)
    try {
        fetch(import.meta.env.VITE_EMAIL_SERVICE_BASE_URL, {
            method: "GET",
        })
    } catch (e) {
        console.error("⚠️ Error waking up email service", e)
    }

    // Get release version and log it
    const releaseVersion = getReleaseVersion()
    logVersionInfo()

    Sentry.init({
        dsn: import.meta.env.VITE_SENTRY_DSN,
        release: releaseVersion,
        integrations: [
            Sentry.browserTracingIntegration(),
            Sentry.replayIntegration(),
        ],
        // Tracing
        tracesSampleRate: 1.0, //  Capture 100% of the transactions
        // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
        tracePropagationTargets: [
            "localhost",
            /^https:\/\/app\.christinamaryhendrietrust\.co\.uk\/.*/,
        ],
        // Session Replay
        replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
        replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    })

    // Set release version as a Sentry tag for easier filtering
    Sentry.setTag("release", releaseVersion)
}

ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(
    <React.StrictMode>
        <QueryClientProvider client={queryClient}>
            <AuthProvider>
                <RouterProvider router={browserRouter} />
                <ReactQueryDevtools initialIsOpen={false} />
            </AuthProvider>
            <Toaster />
        </QueryClientProvider>
    </React.StrictMode>
)
